import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./adminFeatureTour.css";

const SPOTLIGHT_PAD = 10;
const CARD_GAP = 14;
const VIEW_MARGIN = 12;
const CARD_WIDTH = 400;
const CARD_FALLBACK_H = 190;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

/** Exact target bounds + pad, clipped to the viewport (no artificial shift/cap). */
function getSpotlightRect(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top = r.top - SPOTLIGHT_PAD;
  let left = r.left - SPOTLIGHT_PAD;
  let right = r.right + SPOTLIGHT_PAD;
  let bottom = r.bottom + SPOTLIGHT_PAD;

  top = clamp(top, VIEW_MARGIN, vh - VIEW_MARGIN);
  left = clamp(left, VIEW_MARGIN, vw - VIEW_MARGIN);
  right = clamp(right, VIEW_MARGIN, vw - VIEW_MARGIN);
  bottom = clamp(bottom, VIEW_MARGIN, vh - VIEW_MARGIN);

  const width = Math.max(40, right - left);
  const height = Math.max(40, bottom - top);
  if (width < 40 || height < 40) return null;

  return { top, left, width, height, right: left + width, bottom: top + height };
}

function rectsOverlap(a, b, pad = 0) {
  return !(
    a.right + pad <= b.left ||
    a.left - pad >= b.right ||
    a.bottom + pad <= b.top ||
    a.top - pad >= b.bottom
  );
}

/**
 * Pick card position: right → left → bottom → top, scored by free space
 * and whether the card would cover the spotlight.
 */
function getCardPosition(spot, cardW, cardH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const m = VIEW_MARGIN;

  const candidates = [
    {
      side: "right",
      top: spot.top,
      left: spot.right + CARD_GAP,
    },
    {
      side: "left",
      top: spot.top,
      left: spot.left - CARD_GAP - cardW,
    },
    {
      side: "bottom",
      top: spot.bottom + CARD_GAP,
      left: spot.left + spot.width / 2 - cardW / 2,
    },
    {
      side: "top",
      top: spot.top - CARD_GAP - cardH,
      left: spot.left + spot.width / 2 - cardW / 2,
    },
  ];

  let best = null;
  let bestScore = -Infinity;

  for (const c of candidates) {
    let { top, left, side } = c;
    left = clamp(left, m, vw - cardW - m);
    top = clamp(top, m, vh - cardH - m);

    const cardRect = {
      top,
      left,
      width: cardW,
      height: cardH,
      right: left + cardW,
      bottom: top + cardH,
    };

    const overlaps = rectsOverlap(cardRect, spot, 4);
    const space =
      side === "right"
        ? vw - spot.right - m
        : side === "left"
          ? spot.left - m
          : side === "bottom"
            ? vh - spot.bottom - m
            : spot.top - m;

    // Prefer roomy sides; heavily penalize covering the highlight
    let score = space;
    if (overlaps) score -= 10000;
    if (side === "right") score += 40;
    else if (side === "left") score += 30;
    else if (side === "bottom") score += 20;

    if (score > bestScore) {
      bestScore = score;
      best = { top, left, side };
    }
  }

  return best || { top: m, left: m, side: "fallback" };
}

function scrollTargetIntoView(el) {
  if (!el) return;
  try {
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  } catch {
    const parent =
      el.closest(".rules-php-card") ||
      el.closest(".themeupdate-page") ||
      el.parentElement;
    if (parent) parent.scrollTop = Math.max(0, el.offsetTop - 80);
  }
}

/**
 * Shared admin feature tour.
 * Spotlight matches the target; description card docks left/right/top/bottom to avoid overlap.
 */
export default function AdminFeatureTour({ open, onClose, steps = [], onStepChange }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardPos, setCardPos] = useState(null);
  const cardRef = useRef(null);

  const step = steps[stepIndex];
  const isLast = stepIndex >= steps.length - 1;

  const goNext = useCallback(() => {
    if (isLast) onClose?.();
    else setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  }, [isLast, onClose, steps.length]);

  const goBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const placeCard = useCallback((spot) => {
    if (!spot) {
      setCardPos(null);
      return;
    }
    const cardEl = cardRef.current;
    const cardW = Math.min(CARD_WIDTH, window.innerWidth - VIEW_MARGIN * 2);
    const cardH = cardEl?.offsetHeight || CARD_FALLBACK_H;
    setCardPos(getCardPosition(spot, cardW, cardH));
  }, []);

  const measure = useCallback(() => {
    if (!open || !step) {
      setRect(null);
      setCardPos(null);
      return;
    }
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (!el) {
      // This step's target isn't on the page (removed/renamed UI) — skip it
      // instead of freezing on a blank overlay with a hidden card.
      if (isLast) onClose?.();
      else setStepIndex((i) => Math.min(steps.length - 1, i + 1));
      return;
    }
    scrollTargetIntoView(el);
    window.setTimeout(() => {
      const spot = getSpotlightRect(document.querySelector(`[data-tour="${step.target}"]`));
      setRect(spot);
      // Place after paint so cardRef has real height
      window.requestAnimationFrame(() => placeCard(spot));
    }, 240);
  }, [open, step, placeCard, isLast, onClose, steps.length]);

  useLayoutEffect(() => {
    if (!open || !step) return undefined;
    onStepChange?.(step, stepIndex);
    const delay = step.tab ? 280 : 80;
    const t = window.setTimeout(measure, delay);
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, [open, stepIndex, measure, step, onStepChange]);

  // Re-place card when spotlight updates (after first render with content)
  useLayoutEffect(() => {
    if (!rect) return;
    placeCard(rect);
  }, [rect, stepIndex, placeCard]);

  useEffect(() => {
    if (!open) return undefined;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStepIndex(0);
      setRect(null);
      setCardPos(null);
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        goNext();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goBack();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, goNext, goBack, onClose]);

  if (!open || !step || !steps.length) return null;

  const cardStyle = cardPos
    ? { top: cardPos.top, left: cardPos.left }
    : { top: VIEW_MARGIN, left: VIEW_MARGIN, visibility: "hidden" };

  return (
    <div className="admin-tour" role="dialog" aria-modal="true" aria-labelledby="admin-tour-title">
      <div className="admin-tour__shade" aria-hidden />

      {rect ? (
        <div
          className="admin-tour__spotlight"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
          aria-hidden
        />
      ) : null}

      <div
        ref={cardRef}
        className={`admin-tour__card${cardPos?.side ? ` admin-tour__card--${cardPos.side}` : ""}`}
        style={cardStyle}
      >
        <div className="admin-tour__meta">
          Step {stepIndex + 1} of {steps.length}
        </div>
        <h3 id="admin-tour-title" className="admin-tour__title">
          {step.title}
        </h3>
        <p className="admin-tour__body">{step.body}</p>
        <div className="admin-tour__actions">
          <button type="button" className="admin-tour__btn admin-tour__btn--ghost" onClick={onClose}>
            Skip
          </button>
          <div className="admin-tour__nav">
            {stepIndex > 0 ? (
              <button
                type="button"
                className="admin-tour__btn admin-tour__btn--secondary"
                onClick={goBack}
              >
                Back
              </button>
            ) : null}
            <button type="button" className="admin-tour__btn admin-tour__btn--primary" onClick={goNext}>
              {isLast ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function markTourSeen(storageKey) {
  try {
    localStorage.setItem(storageKey, "1");
  } catch {
    /* ignore */
  }
}

export function shouldAutoStartTour(storageKey) {
  try {
    return localStorage.getItem(storageKey) !== "1";
  } catch {
    return false;
  }
}
